import * as THREE from 'three';
import { FirstPersonCamera } from './FirstPersonCamera';
import { createChunkGeometry, updateChunkGeometry } from './ChunkMesh';
import type { AtlasData } from '../wasm/WasmBridge';

// A 16³ section's geometry never escapes its world-space box, so its bounding
// sphere is a constant — set once, never recomputed.
const SECTION_SPHERE_RADIUS = Math.sqrt(8 * 8 * 3);

export class MinecraftRenderer {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene: THREE.Scene;
  private readonly fpCamera: FirstPersonCamera;
  private readonly material: THREE.Material;
  private readonly sectionMeshes = new Map<string, THREE.Mesh>();

  private animFrameId = 0;
  private lastTime = 0;

  constructor(canvas: HTMLCanvasElement, atlas: AtlasData) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: false, powerPreference: 'high-performance' });
    this.renderer.setSize(canvas.clientWidth, canvas.clientHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    this.renderer.setClearColor(0x87ceeb);

    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.Fog(0x87ceeb, 120, 300);

    const aspect = canvas.clientWidth / canvas.clientHeight;
    this.fpCamera = new FirstPersonCamera(canvas, aspect);

    const texture = new THREE.DataTexture(
      new Uint8Array(atlas.pixels),
      atlas.width,
      atlas.height,
      THREE.RGBAFormat,
      THREE.UnsignedByteType
    );
    texture.magFilter = THREE.NearestFilter;
    texture.minFilter = THREE.NearestFilter;
    texture.generateMipmaps = false;
    texture.flipY = false;
    texture.needsUpdate = true;

    // Lighting is baked per-face into vertex colours by the mesher (Minecraft's
    // fixed directional face shading), so the material is unlit.
    this.material = new THREE.MeshBasicMaterial({
      map: texture,
      alphaTest: 0.5,
      side: THREE.FrontSide,
      vertexColors: true,
    });

    window.addEventListener('resize', () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      this.renderer.setSize(w, h);
      this.fpCamera.resize(w / h);
    });
  }

  removeChunkMesh(cx: number, cz: number): void {
    for (let sy = 0; sy < 16; sy++) {
      this.removeSectionMesh(cx, sy, cz);
    }
  }

  addSectionMesh(cx: number, sy: number, cz: number, vertices: Float32Array, indices: Uint32Array): void {
    const key = `${cx},${sy},${cz}`;
    const existing = this.sectionMeshes.get(key);

    if (vertices.length === 0 || indices.length === 0) {
      if (existing) {
        this.scene.remove(existing);
        existing.geometry.dispose();
        this.sectionMeshes.delete(key);
      }
      return;
    }

    if (existing) {
      if (updateChunkGeometry(existing.geometry, vertices, indices)) {
        return;
      }
      this.scene.remove(existing);
      existing.geometry.dispose();
    }

    const geometry = createChunkGeometry(vertices, indices);
    geometry.boundingSphere = new THREE.Sphere(
      new THREE.Vector3(8, sy * 16 + 8, 8),
      SECTION_SPHERE_RADIUS,
    );

    const mesh = new THREE.Mesh(geometry, this.material);
    // Section vertices carry world-y locally; only X/Z translate.
    mesh.position.set(cx * 16, 0, cz * 16);
    mesh.matrixAutoUpdate = false;
    mesh.updateMatrix();
    mesh.frustumCulled = true;

    this.scene.add(mesh);
    this.sectionMeshes.set(key, mesh);
  }

  removeSectionMesh(cx: number, sy: number, cz: number): void {
    const key = `${cx},${sy},${cz}`;
    const mesh = this.sectionMeshes.get(key);
    if (!mesh) return;
    this.scene.remove(mesh);
    mesh.geometry.dispose();
    this.sectionMeshes.delete(key);
  }

  clearAllMeshes(): void {
    for (const mesh of this.sectionMeshes.values()) {
      this.scene.remove(mesh);
      mesh.geometry.dispose();
    }
    this.sectionMeshes.clear();
  }

  start(onFrame: (now: number) => void): void {
    this.lastTime = performance.now();

    const loop = (now: number) => {
      this.animFrameId = requestAnimationFrame(loop);

      const dt = (now - this.lastTime) / 1000;
      this.lastTime = now;

      onFrame(now);
      this.fpCamera.update(dt);
      this.renderer.render(this.scene, this.fpCamera.camera);
    };

    this.animFrameId = requestAnimationFrame(loop);
  }

  stop(): void {
    if (this.animFrameId) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = 0;
    }
  }

  setTrackingExternal(enabled: boolean): void {
    this.fpCamera.setTrackingExternal(enabled);
  }

  setExternalPose(x: number, y: number, z: number, yaw: number, pitch: number): void {
    this.fpCamera.setPose(x, y, z, yaw, pitch);
  }

  dispose(): void {
    this.stop();
    this.clearAllMeshes();
    (this.material as THREE.MeshBasicMaterial).map?.dispose();
    this.material.dispose();
    this.renderer.dispose();
  }
}
